import React, { useEffect, useState } from 'react';
import { RevolvingDot } from 'react-loader-spinner';
import firebaseService from "../firebase.service";

const ARGlbViewer = ({ glbPath, viewerRef, onProgressComplete }) => {
    const [glbUrl, setGlbUrl] = useState(null);
    const [glbProgress, setGlbProgress] = useState(true);
    const [arSupported, setArSupported] = useState(false);
    const [isInARMode, setIsInARMode] = useState(false);

    useEffect(() => {
        const fetchGlb = async () => {
            try {
                const url = await firebaseService.getFileUrl(glbPath);
                setGlbUrl(url);
            } catch (error) {
                // Error fetching GLB URL
                setGlbUrl(null);
            }
        };
        fetchGlb();
    }, [glbPath]);

    // Check for AR support
    useEffect(() => {
        const checkARSupport = async () => {
            try {
                // Check if WebXR is supported
                if ('xr' in navigator) {
                    const isSupported = await navigator.xr.isSessionSupported('immersive-ar');
                    setArSupported(isSupported);
                    // AR Support detected
                } else {
                    // Check for iOS Quick Look support (Safari)
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
                    const supportsQuickLook = isIOS && isSafari;
                    setArSupported(supportsQuickLook);
                    // iOS Quick Look AR Support
                }
            } catch (error) {
                // AR Support check failed
                setArSupported(false);
            }
        };

        checkARSupport();
    }, []);

    // Remove borders and hide only the default AR button (not AR interface controls)
    useEffect(() => {
        const removeBordersAndHideDefaultAR = () => {
            const viewer = viewerRef?.current;
            if (viewer) {
                // Remove borders from model-viewer element
                viewer.style.border = 'none';
                viewer.style.outline = 'none';
                viewer.style.boxShadow = 'none';
                viewer.style.borderRadius = '0';
                viewer.style.borderWidth = '0';
                viewer.style.borderStyle = 'none';
                viewer.style.borderColor = 'transparent';
                
                // Remove borders from child elements but preserve camera controls and AR interface
                const children = viewer.querySelectorAll('*');
                children.forEach(child => {
                    // Don't interfere with canvas, camera controls, or AR interface elements
                    if (!child.tagName || 
                        child.tagName.toLowerCase() === 'canvas' ||
                        child.classList.contains('camera-controls') ||
                        child.getAttribute('part') === 'canvas' ||
                        child.classList.contains('ar-interface') ||
                        child.classList.contains('ar-controls') ||
                        child.getAttribute('part')?.includes('ar-')) {
                        return;
                    }
                    child.style.border = 'none';
                    child.style.outline = 'none';
                    child.style.boxShadow = 'none';
                });

                // Only hide default AR buttons, not our custom one or AR interface elements
                const defaultARButtons = viewer.querySelectorAll('button:not([slot="ar-button"])');
                defaultARButtons.forEach(button => {
                    const buttonText = button.textContent?.toLowerCase() || '';
                    const buttonTitle = button.title?.toLowerCase() || '';
                    const buttonAriaLabel = button.getAttribute('aria-label')?.toLowerCase() || '';
                    const buttonSlot = button.getAttribute('slot') || '';
                    const buttonClass = button.className?.toLowerCase() || '';
                    
                    // Hide only default AR buttons, not our custom one or AR interface buttons
                    if ((buttonSlot === 'ar-button' || 
                        buttonText.includes('ar') || 
                        buttonTitle.includes('ar') || 
                        buttonAriaLabel.includes('ar') ||
                        buttonClass.includes('ar') ||
                        button.getAttribute('part')?.includes('ar')) &&
                        !button.getAttribute('slot') === 'ar-button') {
                        button.style.display = 'none';
                        button.style.visibility = 'hidden';
                        button.style.opacity = '0';
                        button.style.pointerEvents = 'none';
                        button.style.position = 'absolute';
                        button.style.left = '-9999px';
                        button.style.top = '-9999px';
                        button.style.width = '0';
                        button.style.height = '0';
                        button.remove();
                    }
                });

                // Hide any default AR button parts
                const defaultARButtonParts = viewer.querySelectorAll('[part="default-ar-button"]');
                defaultARButtonParts.forEach(part => {
                    part.style.display = 'none';
                    part.style.visibility = 'hidden';
                    part.style.opacity = '0';
                    part.style.position = 'absolute';
                    part.style.left = '-9999px';
                    part.style.top = '-9999px';
                    part.style.width = '0';
                    part.style.height = '0';
                    part.remove();
                });
            }
        };

        // Run immediately and after a short delay to catch dynamically added elements
        removeBordersAndHideDefaultAR();
        const timeoutId = setTimeout(removeBordersAndHideDefaultAR, 100);
        const timeoutId2 = setTimeout(removeBordersAndHideDefaultAR, 500);
        const timeoutId3 = setTimeout(removeBordersAndHideDefaultAR, 1000);

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(timeoutId2);
            clearTimeout(timeoutId3);
        };
    }, [glbUrl, viewerRef]);

    useEffect(() => {
        const updateProgressBar = (event) => {
            if (event.detail.totalProgress === 1) {
                setGlbProgress(false);
                if (onProgressComplete) onProgressComplete();
            }
        };

        const handleARModeChange = (event) => {
            // AR mode changed
            const isARActive = event.detail === 'ar';
            setIsInARMode(isARActive);
            
            // Hide our custom AR button when AR is active
            if (isARActive) {
                const customARButton = viewer.querySelector('button[slot="ar-button"]');
                if (customARButton) {
                    customARButton.style.display = 'none';
                    customARButton.style.visibility = 'hidden';
                    customARButton.style.opacity = '0';
                }
                
                // Ensure AR interface elements are visible
                setTimeout(() => {
                    const arElements = viewer.querySelectorAll('.ar-camera, .ar-interface, .ar-controls, [class*="ar-camera"], [class*="ar-interface"], [class*="ar-controls"]');
                    arElements.forEach(element => {
                        element.style.display = 'block';
                        element.style.visibility = 'visible';
                        element.style.opacity = '1';
                        element.style.pointerEvents = 'auto';
                        element.style.zIndex = '10000';
                    });
                    
                    // Ensure AR buttons are visible
                    const arButtons = viewer.querySelectorAll('button[aria-label*="AR"], button[aria-label*="ar"], button[title*="AR"], button[title*="ar"], button[class*="ar"]:not([slot="ar-button"])');
                    arButtons.forEach(button => {
                        button.style.display = 'block';
                        button.style.visibility = 'visible';
                        button.style.opacity = '1';
                        button.style.pointerEvents = 'auto';
                        button.style.zIndex = '10000';
                    });
                }, 100);
            } else {
                // Show our custom AR button when AR is not active
                const customARButton = viewer.querySelector('button[slot="ar-button"]');
                if (customARButton) {
                    customARButton.style.display = 'flex';
                    customARButton.style.visibility = 'visible';
                    customARButton.style.opacity = '1';
                }
            }
        };

        const viewer = viewerRef?.current;
        if (viewer) {
            viewer.addEventListener('progress', updateProgressBar);
            viewer.addEventListener('ar-status', handleARModeChange);
            
            // Debug: Check if camera controls are properly enabled
            // Check model-viewer camera controls and loaded state
            
            // Ensure camera controls are enabled
            if (!viewer.hasAttribute('camera-controls')) {
                viewer.setAttribute('camera-controls', '');
                // Added camera-controls attribute
            }
            
            // Ensure the viewer can receive pointer events
            viewer.style.pointerEvents = 'auto';
            viewer.style.touchAction = 'pan-x pan-y';
            viewer.style.userSelect = 'none';
            
            // Force enable camera controls via JavaScript
            if (viewer.cameraControls && typeof viewer.cameraControls === 'object') {
                viewer.cameraControls.enabled = true;
                // Camera controls enabled via JavaScript
            } else {
                // Camera controls is a boolean, not an object
            }
        }

        return () => {
            if (viewer) {
                viewer.removeEventListener('progress', updateProgressBar);
                viewer.removeEventListener('ar-status', handleARModeChange);
            }
        };
    }, [viewerRef, onProgressComplete, glbUrl]);

    // Ensure pulse animation starts immediately when component mounts and AR is supported
    useEffect(() => {
        if (!arSupported) return;
        
        const startPulseAnimation = () => {
            // Force restart the animation by temporarily removing and re-adding it
            const arButton = document.querySelector('div[title="View in AR"]');
            if (arButton) {
                // Remove animation temporarily
                arButton.style.animation = 'none';
                // Force reflow by accessing a property that triggers layout
                void arButton.offsetHeight;
                // Re-add animation
                arButton.style.animation = 'pulseGlow 1.8s infinite ease-in-out';
                // AR button pulse animation started
            }
        };

        // Start animation immediately
        startPulseAnimation();
        
        // Also start after a short delay to ensure DOM is ready
        const timeoutId = setTimeout(startPulseAnimation, 100);
        
        return () => {
            clearTimeout(timeoutId);
        };
    }, [glbUrl, arSupported]); // Re-run when glbUrl changes or AR support changes

    // Show loading state if no GLB URL is available
    if (!glbUrl) {
        return (
            <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg w-full max-w-md">
                <div className="text-center">
                    <RevolvingDot
                        visible={true}
                        height="80"
                        width="80"
                        color="#4285F4"
                        ariaLabel="revolving-dot-loading"
                        wrapperStyle={{}}
                        wrapperClass="h-full flex justify-center items-center"
                    />
                    <div className="text-gray-500 text-lg mt-4">Loading 3D Model...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="ar-glb-viewer" style={{ 
            position: 'relative', 
            width: '100%', 
            height: '100%',
            border: 'none',
            outline: 'none'
        }}>
                <model-viewer
                    ref={viewerRef}
                    alt="3D Model with AR Support"
                    src={glbUrl}
                    camera-orbit="-20deg 90deg 100m"
                    camera-controls
                    touch-action="pan-x pan-y"
                    interaction-policy="allow-when-focused"
                    loading="eager"
                    ar
                    ar-modes="quick-look webxr scene-viewer"
                    ar-scale="auto"
                    ar-placement="floor"
                    ios-src={glbUrl}
                style={{ 
                    width: "100%", 
                    height: "100%",
                    minHeight: "45vh",
                    border: "none",
                    outline: "none",
                    borderWidth: "0",
                    borderStyle: "none",
                    borderColor: "transparent",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    borderLeft: "none",
                    boxShadow: "none",
                    pointerEvents: "auto"
                }}
            >
                <div slot="progress-bar" style={{ display: !glbProgress && 'none' }} className="custom-progress-bar h-full">
                    <RevolvingDot
                        visible={true}
                        height="80"
                        width="80"
                        color="#4285F4"
                        ariaLabel="revolving-dot-loading"
                        wrapperStyle={{}}
                        wrapperClass="h-full flex justify-center items-center"
                    />
                </div>
                
                        {arSupported && !isInARMode && (
                            <button 
                                slot="ar-button"
                                style={{
                                    position: 'absolute',
                                    bottom: '5px',
                                    right: '10px',
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                    outline: 'none',
                                    zIndex: 9999,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0',
                                    margin: '0',
                                    transition: 'all 0.3s ease',
                                    transform: 'scale(1)',
                                    animation: 'pulseGlow 1.8s infinite ease-in-out'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'scale(1.1)';
                                    e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3)';
                                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'scale(1)';
                                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                                    e.target.style.backgroundColor = 'transparent';
                                }}
                                onMouseDown={(e) => {
                                    e.target.style.transform = 'scale(0.95)';
                                }}
                                onMouseUp={(e) => {
                                    e.target.style.transform = 'scale(1.1)';
                                }}
                                title="View in AR"
                            >
                                <img 
                                    src={`/argentina.png?v=${Date.now()}&t=${Math.random()}`} 
                                    alt="AR" 
                                    style={{
                                        width: '36px',
                                        height: '36px',
                                        objectFit: 'contain',
                                        display: 'block',
                                        pointerEvents: 'none'
                                    }}
                                    onLoad={() => {/* Argentina icon loaded successfully */}}
                                    onError={(e) => {
                                        // Argentina icon failed to load
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </button>
                        )}
            </model-viewer>
        </div>
    );
};

export default ARGlbViewer;
